import React, { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileText } from "lucide-react";
import { useReportingData } from "@/hooks/useReportingData";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import Papa from "papaparse";
import { toast } from "sonner";

export interface ReportExportPanelProps {
  dateRange?: { from: Date; to: Date };
}

const exportToCSV = (data: any[], filename: string) => {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default function ReportExportPanel({ dateRange }: ReportExportPanelProps) {
  const { revenueData, attendanceData, enrollmentData } = useReportingData(dateRange);
  // Add more queries for capacity, retention, schedule performance as needed
  const [exporting, setExporting] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const handleExportCSV = () => {
    setExporting(true);
    // Combine all data into one export object
    const exportData = [
      ...revenueData,
      ...attendanceData,
      ...enrollmentData,
      // Add more as needed
    ];
    exportToCSV(exportData, "hivetivity-report.csv");
    setExporting(false);
  };

  const handleExportPDF = async () => {
    setExportingPDF(true);
    toast.info("Generating PDF report...");
    try {
      // Find the main reporting dashboard element
      const dashboard = document.querySelector(".space-y-6.p-6");
      if (!dashboard) throw new Error("Could not find reporting dashboard to export");
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      const canvas = await html2canvas(dashboard as HTMLElement, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 40;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 20, 40, imgWidth, imgHeight);
      pdf.setFontSize(18);
      pdf.text("Hivetivity Reporting Dashboard", 20, 30);
      pdf.save("hivetivity-report.pdf");
      toast.success("PDF report ready!");
    } catch (err) {
      toast.error("Failed to generate PDF report");
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <Card className="shadow-sm border-gray-100/40 bg-white/80 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-500" />
          Export & Shareable Reports
        </CardTitle>
      </CardHeader>
      <CardContent className="flex gap-4 items-center">
        <Button onClick={handleExportCSV} disabled={exporting} variant="outline" className="flex gap-2">
          <Download className="h-4 w-4" />
          Download CSV
        </Button>
        <Button onClick={handleExportPDF} disabled={exportingPDF} variant="outline" className="flex gap-2">
          <Download className="h-4 w-4" />
          Download PDF
        </Button>
        {exporting && <span className="text-xs text-muted-foreground ml-2">Exporting...</span>}
        {exportingPDF && <span className="text-xs text-muted-foreground ml-2">Generating PDF...</span>}
      </CardContent>
    </Card>
  );
}
